import { Dialog, Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react'
import { Bars3Icon, ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline'
import Button from '../layout/Button'
import { useState, useEffect, Fragment } from 'react'
import Modal from '../layout/Modal'
import TextInput from '../forms/TextInput'
import { addAlert } from '../../slices/alert'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { closeLoginPrompt, getAuth, login, logout, register, sendVerificationEmail, UserState } from '../../slices/auth'
import RegisterModal from './RegisterModal'
import { extractApiError, isValid, validateEmail, validateRequired } from '../../utils/validation'

type LoginErrors = { username: string | null; password: string | null; form: string | null };

const NO_LOGIN_ERRORS: LoginErrors = { username: null, password: null, form: null };

interface MenuItem {
  name: string;
  href: string;
}

interface NavigationItem {
  name: string;
  href: string;
  current: boolean;
  main: boolean;
  menu: MenuItem[];
  displayClass: string;
}

const getNavigation = (user: UserState | null): NavigationItem[] => {
  const baseNavigation: NavigationItem[] = [
    { name: 'Topics', href: '/topics-and-collections', current: true, main: true, menu: [], displayClass: "" },
    { name: 'Collections', href: '/topics-and-collections', current: false, main: true, menu: [], displayClass: "" },
    { name: 'Events', href: '/events', current: false, main: true, menu: [], displayClass: "" },
    { name: 'Directory', href: '/directory', current: false, main: true, menu: [], displayClass: "" },
    { name: 'Search', href: '/search', current: false, main: true, menu: [], displayClass: "" },
  ];

  // Add Pipeline link for users with pipeline access
  const pipelineGroups = ['Admin', 'Content_Manager', 'Content_Reviewer', 'Content_Creator', 'Analyst'];
  const hasPipelineAccess = user?.auth?.groups?.some(([_, groupName]: [string, string]) => 
    pipelineGroups.includes(groupName)
  );

  if (hasPipelineAccess) {
    baseNavigation.push({
      name: 'Pipeline',
      href: '#!',
      current: false,
      main: true,
      menu: [
        { name: "Dashboard", href: "/pipeline/dashboard" },
        { name: "Ingest Content", href: "/pipeline/ingest" }
      ],
      displayClass: ""
    });
  }

  baseNavigation.push(
    { name: 'Speaker', href: '/speaking', current: false, main: false, menu: [], displayClass: "hidden xl:block" },
    {
      name: 'About', href: '#!', current: false, main: false, menu: [
        { name: "About", href: "/about" },
        // { name: "Blog", href: "/blog" },
        { name: "Contact", href: "/contact" }
      ], displayClass: "hidden xl:block"
    }
  );

  return baseNavigation;
}

const getMobileNavigation = (user: UserState | null): NavigationItem[] => {
  const baseMobileNav: NavigationItem[] = [
    { name: 'Topics', href: '/topics-and-collections', current: true, main: true, menu: [], displayClass: "" },
    { name: 'Collections', href: '/topics-and-collections', current: false, main: true, menu: [], displayClass: "" },
    { name: 'Events', href: '/events', current: false, main: true, menu: [], displayClass: "" },
    { name: 'Directory', href: '/directory', current: false, main: true, menu: [], displayClass: "" },
    { name: 'Search', href: '/search', current: false, main: true, menu: [], displayClass: "" },
  ];

  // Add Pipeline links for users with pipeline access
  const pipelineGroups = ['Admin', 'Content_Manager', 'Content_Reviewer', 'Content_Creator', 'Analyst'];
  const hasPipelineAccess = user?.auth?.groups?.some(([_, groupName]: [string, string]) => 
    pipelineGroups.includes(groupName)
  );

  if (hasPipelineAccess) {
    baseMobileNav.push(
      { name: 'Pipeline Dashboard', href: '/pipeline/dashboard', current: false, main: false, menu: [], displayClass: "" },
      { name: 'Ingest Content', href: '/pipeline/ingest', current: false, main: false, menu: [], displayClass: "" }
    );
  }

  baseMobileNav.push(
    { name: 'Speaker', href: '/speaking', current: false, main: false, menu: [], displayClass: "" },
    { name: 'About', href: '/about', current: false, main: false, menu: [], displayClass: "" },
    { name: 'Blog', href: '/blog', current: false, main: false, menu: [], displayClass: "" },
    { name: 'Contact', href: '/contact', current: false, main: false, menu: [], displayClass: "" }
  );

  return baseMobileNav;
}

export default function Navbar({
  mode = "transparent"
}: {
  mode?: "transparent" | "solid"
}) {

  const dispatch = useAppDispatch();

  // Get app level state from redux store
  const { user, isAuthenticated, loading, loginPromptOpen } = useAppSelector((state) => state.auth);

  // Component level state
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginErrors, setLoginErrors] = useState<LoginErrors>(NO_LOGIN_ERRORS);
  const [loginState, setLoginState] = useState({
    username: "",
    password: "",
  });
  const [openMenuName, setOpenMenuName] = useState<string | null>(null);

  // Open the login modal on behalf of anything else on the page that needs a
  // signed-in visitor -- a members-only card or collection. The flag is lowered
  // straight away so dismissing the modal does not immediately reopen it.
  useEffect(() => {
    if (!loginPromptOpen) return;
    setShowLoginModal(true);
    dispatch(closeLoginPrompt());
  }, [loginPromptOpen, dispatch]);

  // Ensure auth is loaded
  useEffect(() => {
    if (isAuthenticated && !user && !loading) {
      dispatch(getAuth());
    }
  }, [isAuthenticated, user, loading, dispatch]);

  // Close dropdown only on mousedown outside the open menu (not on hover / moving to panel)
  useEffect(() => {
    if (!openMenuName) return;
    const handleMouseDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      const root = t.closest("[data-nav-dropdown-root]");
      const rootName = root?.getAttribute("data-nav-dropdown-root");
      if (!rootName || rootName !== openMenuName) {
        setOpenMenuName(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [openMenuName]);

  // Functions
  function updateLoginField(field: "username" | "password", value: string) {
    setLoginState((previous) => ({ ...previous, [field]: value }));
    setLoginErrors((previous) => ({ ...previous, [field]: null, form: null }));
  }

  function validateLogin() {
    return {
      // Accounts are registered with the email as the username, so the login
      // identifier is an email and is checked as one.
      username: validateEmail(loginState.username),
      password: validateRequired(loginState.password, "Password"),
      form: null as string | null,
    };
  }

  async function triggerLogin(e: any) {
    e.preventDefault()
    const { username, password } = loginState;

    const formErrors = validateLogin();
    setLoginErrors(formErrors);
    if (!isValid(formErrors)) {
      return;
    }

    const res: any = await dispatch(login({ username, password }))
    if (res.payload?.status === 200) {
      dispatch(getAuth())
      dispatch(addAlert("Login successful!", "mint-green"))
      setLoginState({ username: "", password: "" })
      setLoginErrors(NO_LOGIN_ERRORS)
      setShowLoginModal(false)
      return;
    }

    // Keep the modal open so the credentials can be corrected in place —
    // previously a typo closed it and the user had to start over.
    const status = res.payload?.status;
    let message: string;
    if (status === 401) {
      // Deliberately does not say which of the two was wrong: that would confirm
      // to a stranger whether an email is registered here.
      message = "Incorrect email or password. Please try again.";
    } else if (status === 429) {
      message = "Too many login attempts. Please wait a moment and try again.";
    } else {
      message = extractApiError(res.payload, "Login failed, please try again.");
    }
    setLoginErrors({ username: null, password: null, form: message })
  }

  // Contant variables
  const containerClasses =
    mode === "transparent"
      ? "bg-gradient-to-b from-black/70 to-transparent backdrop-blur-sm z-20 fixed w-full top-0"
      : "bg-off-white z-20 fixed w-full top-0";

  const logoTextColour = mode === "transparent" ? "white" : "dark-blue";
  const navTextColour = mode === "transparent" ? "white" : "dark-grey";
  const loginButtonColour = mode === "transparent" ? "white" : "dark-grey";
  return (
    <Disclosure as="nav" className={containerClasses}>

      {/* Register modal */}
      <RegisterModal show={showRegisterModal} setShow={setShowRegisterModal} />

      {/* Login modal */}
      <Modal
        show={showLoginModal}
        setShow={setShowLoginModal}
        confirmText="Login!"
        cancelText="Cancel"
        confirmHandler={() => { }}
        showActionButtons={false}
        showTriggerButton={false}
      >
        <div className="overflow-auto px-1">
          <div className="text-center">
            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-black">
              Log in
            </Dialog.Title>
            <div className="mt-10 grid grid-cols-1 gap-y-4">

              {/* Username */}
              <TextInput
                id="login-username"
                type="email"
                label=""
                borderClass="border border-black"
                showLabel={false}
                autoComplete="email"
                onChange={(e) => updateLoginField("username", e.target.value)}
                onBlur={() => setLoginErrors((previous) => ({ ...previous, username: validateEmail(loginState.username) }))}
                error={loginErrors.username}
                placeholder="Email"
                value={loginState.username}
              />

              {/* Password */}
              <TextInput
                id="login-password"
                type="password"
                label=""
                borderClass="border border-black"
                showLabel={false}
                autoComplete="current-password"
                onChange={(e) => updateLoginField("password", e.target.value)}
                error={loginErrors.password}
                placeholder="Password"
                value={loginState.password}
              />

              {/* Whole-form error, e.g. wrong credentials */}
              {loginErrors.form && (
                <p role="alert" className="text-sm text-left text-orange">
                  {loginErrors.form}
                </p>
              )}

              {/* Submit button */}
              <Button
                bgColour="dark-blue"
                borderColour="dark-blue"
                onClick={(e) => triggerLogin(e)}
              >
                <span className="text-white">Continue</span>
              </Button>

              {/* Forgot password */}
              <p className="text-black">
                <a className="text-dark-blue underline cursor-pointer text-sm sm:text-base" href="/forgot-my-password">Forgot your password?</a>
              </p>

            </div>
          </div>
        </div>
      </Modal>

      <div className="mx-auto px-2 md:px-6 lg:px-8 z-50">
        <div className={`relative flex h-${mode === "transparent" ? "20" : "20"} items-start pt-4 justify-between`}>
          {/* Mobile menu button.
              Switched from `md` (768px) to `lg` (1024px). The desktop row has to
              hold five links, the staff-only Pipeline dropdown and two auth
              buttons — measured at ~1205px for a signed-in staff user, which does
              not fit 768px and does not fit 1024px either. Below lg the burger
              menu carries them instead. */}
          <div className="absolute inset-y-0 left-0 flex items-center lg:hidden">
            <DisclosureButton
              className={`group relative inline-flex items-center justify-center rounded-md p-2 text-${navTextColour} focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white`}
            >
              <span className="absolute -inset-0.5" />
              <span className="sr-only">Open main menu</span>
              <Bars3Icon
                aria-hidden="true"
                className={`block size-6 group-data-[open]:hidden text-${navTextColour}`}
              />
              <XMarkIcon
                aria-hidden="true"
                className={`hidden size-6 group-data-[open]:block text-${navTextColour}`}
              />
            </DisclosureButton>
          </div>

          {/* Logo */}
          <div className="flex flex-1 min-w-0 items-center justify-center lg:items-stretch lg:justify-start">
            <div className="flex shrink-0 items-center">
              <a href="/" className="flex items-center flex-nowrap">
                <img
                  alt="RaiseDiversity"
                  src="/img/logo/logo.svg"
                  className="h-12 w-auto"
                />
                {/* The wordmark measures 237px at text-3xl and used to appear at
                    `lg` — the exact width where the link row is already full, so
                    it pushed the auth buttons off the right edge. Held back to
                    `xl` and only shown full-size at `2xl`, where there is room. */}
                <span
                  className={`hidden xl:block text-${logoTextColour} text-xl 2xl:text-3xl font-extrabold tracking-wider`}
                >
                  RAISE<span className="font-light">DIVERSITY</span>
                </span>
              </a>
            </div>
            {/* Main Nav Links */}
            <div className="hidden lg:ml-6 lg:block flex w-full min-w-0 my-auto">
              {/* <div className="grid grid-cols-4 xl:grid-cols-8 items-center gap-x-2"> */}
              <div className="flex w-full gap-x-2">
                {/* {navigation.filter((item, idx) => idx <= 3).map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    className={`text-${navTextColour} hover:bg-white/10 rounded-md px-3 py-2 text-sm font-bold w-fit mx-auto`}
                  >
                    {item.name}
                  </a>
                ))} */}
                {/* <div className="w-full"/> */}
                {/* {navigation.filter((item, idx) => idx >= 4).map((item) => (
                  <a
                    key={item.name}
                    href={item.href}
                    aria-current={item.current ? "page" : undefined}
                    className={`hidden xl:block text-${navTextColour} hover:bg-white/10 rounded-md px-3 py-2 text-sm font-bold w-fit mx-auto`}
                  >
                    {item.name}
                  </a>
                ))} */}
                {getNavigation(user).map((item) => (
                  <Fragment key={item.name}>
                    {item.menu.length === 0 ? (
                      <a
                        href={item.href}
                        className={`${item.displayClass} shrink-0 whitespace-nowrap text-${navTextColour} hover:bg-white/10 rounded-md px-2 2xl:px-3 py-2 text-sm 2xl:text-base font-bold w-fit mx-auto`}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <div
                        className={`${item.displayClass} shrink-0 relative`}
                        data-nav-dropdown-root={item.name}
                      >
                        <button
                          type="button"
                          className={`whitespace-nowrap text-${navTextColour} hover:bg-white/10 rounded-md px-2 2xl:px-3 py-2 text-sm 2xl:text-base font-bold w-fit mx-auto`}
                          aria-expanded={openMenuName === item.name}
                          aria-haspopup="true"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuName(openMenuName === item.name ? null : item.name);
                          }}
                        >
                          <span className="flex items-center gap-x-2">
                            {item.name}
                            {openMenuName === item.name ? (
                              <ChevronUpIcon className={`h-4 w-4 text-${mode === "transparent" ? "white" : "dark-grey"}`} />
                            ) : (
                              <ChevronDownIcon className={`h-4 w-4 text-${mode === "transparent" ? "white" : "dark-grey"}`} />
                            )}
                          </span>
                        </button>

                        {openMenuName === item.name && (
                          <div className="absolute top-full left-0 pt-1 w-44 z-50">
                            <div className="rounded-md bg-white shadow-lg py-2">
                              <div className="flex flex-col">
                                {item.menu.map((link, idx) => (
                                  <a
                                    key={link.name}
                                    href={link.href}
                                    onClick={() => setOpenMenuName(null)}
                                    className={`text-black hover:bg-gray-50 px-4 py-3 text-sm font-medium text-left ${idx > 0 ? "border-t border-gray-100" : ""}`}
                                  >
                                    {link.name}
                                  </a>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>

            </div>
          </div>

          {/* Auth buttons */}
          {loading ? null : isAuthenticated && user !== null ? (
            <div className="invisible sm:visible absolute inset-y-0 right-0 flex shrink-0 items-center pr-2 lg:static lg:inset-auto lg:ml-6 lg:pr-0 gap-x-2 lg:mt-2">
              <Button
                onClick={() => {
                  window.location.href = "/account"
                }}
                shadow="md"
              >
                <span className="text-nowrap">My Account</span>
              </Button>
              <Button
                bgColour="transparent"
                borderColour={loginButtonColour}
                onClick={() => {
                  dispatch(logout());
                }}
                shadow="md"
              >
                <span className={`text-nowrap text-${loginButtonColour}`}>
                  Logout
                </span>
              </Button>
            </div>
          ) : (
            <div className="invisible sm:visible absolute inset-y-0 right-0 flex shrink-0 items-center pr-2 lg:static lg:inset-auto lg:ml-6 lg:pr-0 gap-x-2 lg:mt-2">
              <Button
                onClick={() => {
                  setShowRegisterModal(true);
                }}
                shadow="md"
              >
                <span className="text-nowrap">Sign up</span>
              </Button>
              <Button
                bgColour="transparent"
                borderColour={loginButtonColour}
                onClick={() => {
                  setShowLoginModal(true);
                }}
                shadow="md"
              >
                <span className={`text-nowrap text-${loginButtonColour}`}>
                  Log in
                </span>
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Mobile panel */}
      <DisclosurePanel className="lg:hidden">
        <div className="space-y-1 px-2 pb-3 pt-2">
          {getMobileNavigation(user).map((item) => (
            <DisclosureButton
              key={item.name}
              as="a"
              href={item.href}
              aria-current={item.current ? "page" : undefined}
              className={`text-${navTextColour} block rounded-md px-3 py-2 text-base font-medium`}
            >
              {item.name}
            </DisclosureButton>
          ))}

          {/* Auth buttons */}
          {loading ? null : isAuthenticated && user !== null ? (
            <div className="flex items-center pr-2 md:static md:inset-auto md:ml-6 md:pr-0 gap-x-2 md:mt-2">
              <Button
                onClick={() => {
                  window.location.href = "/account"
                }}
                shadow="md"
              >
                <span className="text-nowrap">My Account</span>
              </Button>
              <Button
                bgColour="transparent"
                borderColour={loginButtonColour}
                onClick={() => {
                  dispatch(logout());
                }}
                shadow="md"
              >
                <span className={`text-nowrap text-${loginButtonColour}`}>
                  Logout
                </span>
              </Button>
            </div>
          ) : (
            <div className="flex items-center pr-2 md:static md:inset-auto md:ml-6 md:pr-0 gap-x-2 md:mt-2">
              <Button
                onClick={() => {
                  setShowRegisterModal(true);
                }}
                shadow="md"
              >
                <span className="text-nowrap">Sign up</span>
              </Button>
              <Button
                bgColour="dark-grey"
                borderColour="dark-grey"
                textColour="white"
                onClick={() => {
                  setShowLoginModal(true);
                }}
                shadow="md"
              >
                <span className={`text-nowrap text-white`}>
                  Log in
                </span>
              </Button>
            </div>
          )}
        </div>
      </DisclosurePanel>
    </Disclosure>
  );
}